import type { CSSProperties, ReactNode } from 'react';
import { cn } from './cn';

/**
 * Ant Design `Statistic` replacement (title + value + prefix/suffix). For richer KPI
 * cards prefer `StatCard`; this exists so plain `<Statistic>` usages migrate cleanly.
 */
export function Statistic({
  title, value, precision, prefix, suffix, formatter, valueStyle, className, groupSeparator = ',',
}: {
  title?: ReactNode;
  value?: number | string;
  precision?: number;
  prefix?: ReactNode;
  suffix?: ReactNode;
  formatter?: (v: number | string) => ReactNode;
  valueStyle?: CSSProperties;
  className?: string;
  groupSeparator?: string;
}) {
  let display: ReactNode;
  if (formatter) display = formatter(value ?? 0);
  else if (typeof value === 'number') {
    const n = precision != null ? value.toFixed(precision) : String(value);
    display = groupSeparator ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: precision ?? 0, maximumFractionDigits: precision ?? 20 }) : n;
  } else display = value;

  return (
    <div className={className}>
      {title != null && <div className="mb-1 text-xs text-muted">{title}</div>}
      <div className="flex items-baseline gap-1 type-metric text-content" style={valueStyle}>
        {prefix != null && <span className="text-[0.8em] text-muted [&>svg]:size-[0.85em]">{prefix}</span>}
        <span>{display}</span>
        {suffix != null && <span className="text-[0.7em] text-muted">{suffix}</span>}
      </div>
    </div>
  );
}
